import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useCalculationForm } from '@/components/calculation-form/composable/useCalculationForm'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import { useBuildingBlockStore } from '@/stores/buildingBlock'
import { useTransportConfigStore } from '@/stores/transportConfig'
import { useCalculationStore } from '@/stores/calculation'
import { useConfirmDeleteStore } from '@/stores/confirmDelete'
import { useBuildingBlockDeletion } from '@/components/building-block-form/composable/useBuildingBlockDeletion'
import type { BuildingBlockDto, ProductDto } from '@/stores/buildingBlock'
import {
  buildAvailableCategories,
  buildSelectedBlocks,
  buildSelectionChangedMessages,
  buildSelectionMissingMessages,
  resolveSelectionNoticeTitle,
  type Category,
  type SelectedBlock
} from '@/components/calculation-form/building-block-selection/utils/buildingBlockSelectionUtils'

export function useBuildingBlockSelection(props: CalculationFormMixinProps) {
  const quantityBuildingBlockStore = useQuantityBuildingBlockStore()
  const transportConfigStore = useTransportConfigStore()
  const calculationStore = useCalculationStore()
  const buildingBlockStore = useBuildingBlockStore()
  const confirmDeleteStore = useConfirmDeleteStore()
  const { deleteBuildingBlock } = useBuildingBlockDeletion()
  const route = useRoute()
  const calculationId = computed(() => Number(route.params.calculationId))

  const { items: buildingBlockItems } = storeToRefs(buildingBlockStore)
  const { buildingBlockSelectionSwitchNotice } = storeToRefs(calculationStore)

  const { isValid, isReadOnly } = useCalculationForm(props)

  const calculationBlocks = computed(() =>
    (buildingBlockItems.value as BuildingBlockDto[]).filter(
      (block) => block.calculation === calculationId.value
    )
  )

  const availableCategories = computed<Category[]>(() =>
    buildAvailableCategories(calculationBlocks.value)
  )

  const selectedBlocks = computed<SelectedBlock[]>(() =>
    buildSelectedBlocks(calculationBlocks.value)
  )

  const selectionNoticeTitle = computed(() =>
    resolveSelectionNoticeTitle(buildingBlockSelectionSwitchNotice.value)
  )
  const selectionMissingMessages = computed(() =>
    buildSelectionMissingMessages(buildingBlockSelectionSwitchNotice.value)
  )
  const selectionChangedMessages = computed(() =>
    buildSelectionChangedMessages(buildingBlockSelectionSwitchNotice.value)
  )

  watch(
    selectedBlocks,
    (blocks) => {
      isValid.value = blocks.length > 0
    },
    { deep: true, immediate: true }
  )

  const handleAddBlock = async (_category: Category, block: BuildingBlockDto) => {
    await buildingBlockStore.setSelected(block, true)
  }

  const detachBlockReferences = async (blockId: number) => {
    for (const quantity of quantityBuildingBlockStore.items) {
      if (quantity.building_block === blockId) {
        await quantityBuildingBlockStore.update({
          ...quantity,
          building_block: null,
          unit_mismatch_accepted_key: null
        })
      }
    }

    for (const config of transportConfigStore.items) {
      if (config.building_block === blockId && !config.apply_to_all_building_block) {
        await transportConfigStore.update({
          ...config,
          building_block: null,
          apply_to_all_building_block: true
        })
      }
    }
  }

  const refetchSelectionStores = async () => {
    await Promise.all([
      quantityBuildingBlockStore.fetchItems({ calculation: calculationId.value }),
      transportConfigStore.fetchItems?.({ calculation: calculationId.value }),
      buildingBlockStore.fetchItems({ calculation: calculationId.value })
    ])
  }

  const handleRemoveBlock = async (block: SelectedBlock) => {
    await buildingBlockStore.setSelected(block, false)
    await detachBlockReferences(block.id)
    await refetchSelectionStores()
  }

  const handleDeleteBlock = async (_category: Category, block: BuildingBlockDto) => {
    const confirmed = await confirmDeleteStore.confirm({
      name: block.name,
      message:
        'Delete this building block? It will be unlinked from design links and its transport configurations will be removed.'
    })
    if (!confirmed) return

    await deleteBuildingBlock(block, calculationId.value)
  }

  const showSearchMaterialDialog = ref(false)

  const handleAddMaterialFromSearch = async (product: ProductDto) => {
    await buildingBlockStore.createFromProduct(calculationId.value, product.id, true)
    await buildingBlockStore.fetchItems({ calculation: calculationId.value })
  }

  return {
    isReadOnly,
    availableCategories,
    selectedBlocks,
    selectionNoticeTitle,
    selectionMissingMessages,
    selectionChangedMessages,
    showSearchMaterialDialog,
    handleAddBlock,
    handleRemoveBlock,
    handleDeleteBlock,
    handleAddMaterialFromSearch
  }
}
